import React from "react";
import classNames from "classnames";
import { CardHeader } from "@shared/ui/card-header";
import { useWindowControls } from "../hooks/useWindowControls";
import { overlayTitleText } from "../overlayTitleText";
import "./MobileWindowFrame.scss";

/**
 * Модификатор content-area для form window: без padding и без внешнего scroll.
 * Подключается автоматически через `defineMobileFormWindow`.
 */
export const MOBILE_WINDOW_FRAME_FORM_CONTENT_CLASS = "mobile_window_frame__content--form";

/** Пропсы {@link MobileWindowFrame}. */
export interface MobileWindowFrameProps {
    title?: React.ReactNode;
    supportingTitle?: React.ReactNode;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    contentClassName?: string;
    footerClassName?: string;
    onRequestClose?: () => void;
    closeButtonLabel?: string;
}

/**
 * Полноэкранная (ТСД) оболочка window-overlay: header, content, optional footer. \
 * Для form windows content получает {@link MOBILE_WINDOW_FRAME_FORM_CONTENT_CLASS}; scroll — в {@link FormPanel}.
 */
export const MobileWindowFrame: React.FC<MobileWindowFrameProps> = ({
    title,
    supportingTitle,
    children,
    footer,
    className,
    contentClassName,
    footerClassName,
    onRequestClose,
}) => {
    const windowControls = useWindowControls();
    const handleClose = onRequestClose ?? (() => windowControls.close());

    const headerText = overlayTitleText(title);
    const supportingText = overlayTitleText(supportingTitle);

    return (
        <div
            className={classNames("mobile_window_frame", className, {
                mobile_window_frame__fullscreen: windowControls.placement === "fullscreen",
                mobile_window_frame__center: windowControls.placement === "center",
            })}
            role="dialog"
            aria-modal={windowControls.blocking}
            style={{ zIndex: windowControls.zIndex }}
        >
            {(title != null || onRequestClose != null) && (
                <div className="mobile_window_frame__header">
                    <CardHeader density="compact" text={headerText} supportingText={supportingText} onClose={handleClose} />
                </div>
            )}
            <div className={classNames("mobile_window_frame__content", "custom_scrollbar", contentClassName)}>
                {children}
            </div>
            {footer != null && <div className={classNames("mobile_window_frame__footer", footerClassName)}>{footer}</div>}
        </div>
    );
};
